import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";

// Shown when an account exists but its email address was never confirmed —
// on sign-in (correct password, unconfirmed address) and on sign-up (the
// address is taken by an unconfirmed account). Both cases need the same way
// out: send the verification link again.
const ResendVerificationNotice = ({ email, description }) => {
  const { resendVerificationEmail } = useAuth();
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState(null); // { type: "success" | "error", text }
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = useCallback(async () => {
    if (!email || isSending || cooldown > 0) return;
    setIsSending(true);
    setStatus(null);
    try {
      const result = await resendVerificationEmail(email);
      if (result.success) {
        setStatus({
          type: "success",
          text: `Verification link sent to ${email}. Check your inbox, including spam.`,
        });
        setCooldown(60);
      } else {
        setStatus({
          type: "error",
          text:
            result.error ||
            "We couldn't send the verification email. Please try again in a moment.",
        });
        if (result.retryAfterSeconds) setCooldown(result.retryAfterSeconds);
      }
    } finally {
      setIsSending(false);
    }
  }, [email, isSending, cooldown, resendVerificationEmail]);

  return (
    <div className="mt-3 rounded-lg border border-jacarta-400 bg-white p-3 dark:border-jacarta-600 dark:bg-jacarta-700">
      <p className="text-sm text-grayish dark:text-white">{description}</p>
      <button
        type="button"
        onClick={handleResend}
        disabled={isSending || cooldown > 0 || !email}
        className="mt-2 text-sm font-bold text-accent underline hover:text-accent-dark disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSending
          ? "Sending..."
          : cooldown > 0
            ? `Resend verification email (${cooldown}s)`
            : "Resend verification email"}
      </button>
      {status && (
        <p
          className={`mt-2 text-xs leading-tight ${
            status.type === "success"
              ? "text-green-600 dark:text-green-400"
              : "text-red-600 dark:text-red-400"
          }`}
        >
          {status.text}
        </p>
      )}
    </div>
  );
};

export default ResendVerificationNotice;
